export default function TermsPage() {
  const lastUpdated = 'April 2026'

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
        <p className="text-muted-foreground text-sm mb-10">Last updated: {lastUpdated}</p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section>
            <p className="text-muted-foreground">
              These Terms of Service ("Terms") govern your access to and use of RoleRound
              ("Service"), operated by RoleRound ("we", "us", "our"). By creating an account or
              using the Service, you agree to these Terms. If you do not agree, do not use the
              Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using RoleRound, you confirm that you are at least 13 years old, that
              you have read and understood these Terms, and that you agree to be bound by them. If
              you are using RoleRound on behalf of an organization, you represent that you have the
              authority to bind that organization to these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground">
              RoleRound is an AI-powered behavioral interview practice platform. The Service
              generates interview questions, evaluates written responses using the STAR framework
              (Situation, Task, Action, Result), and provides coaching feedback. The Service is
              intended for educational and practice purposes only. Feedback generated by RoleRound
              is AI-generated and does not constitute professional career advice, hiring assessment,
              or any guarantee of interview outcomes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Account Registration and Eligibility</h2>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside">
              <li>You must be at least 13 years old to use RoleRound.</li>
              <li>You must provide accurate and complete information when creating an account.</li>
              <li>You are responsible for maintaining the security of your account credentials.</li>
              <li>
                You may not share your account with others or create accounts on behalf of other
                people without their consent.
              </li>
              <li>
                We reserve the right to suspend or terminate accounts that violate these Terms.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Subscriptions and Payments</h2>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside">
              <li>
                <span className="text-foreground font-medium">Free tier</span> — includes 3 practice
                sessions per rolling 30-day window at no cost. No credit card required.
              </li>
              <li>
                <span className="text-foreground font-medium">Pro subscription</span> — includes
                unlimited sessions and additional features, billed monthly or annually via Stripe.
              </li>
              <li>
                Subscription fees are billed at the start of each billing period and are
                non-refundable except as required by law.
              </li>
              <li>
                You may cancel your subscription at any time. Cancellation takes effect at the end
                of the current billing period; you retain Pro access until then.
              </li>
              <li>
                We reserve the right to change pricing with 30 days' notice. Continued use after
                notice constitutes acceptance of new pricing.
              </li>
              <li>All payments are processed by Stripe. We do not store your card details.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Acceptable Use</h2>
            <p className="text-muted-foreground mb-3">You agree not to:</p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside">
              <li>
                Use RoleRound to generate content intended to deceive employers or misrepresent your
                qualifications.
              </li>
              <li>
                Attempt to circumvent session limits, access controls, or subscription gating.
              </li>
              <li>
                Scrape, reverse-engineer, or copy the Service's prompts, scoring logic, or feedback
                system.
              </li>
              <li>Use automated tools to interact with the Service at scale.</li>
              <li>
                Submit harmful, illegal, or offensive content through the interview interface.
              </li>
              <li>
                Resell, redistribute, or commercialize AI-generated feedback without our written
                permission.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. AI-Generated Content</h2>
            <p className="text-muted-foreground">
              Interview questions and feedback are generated by AI (Anthropic Claude). AI-generated
              content may contain inaccuracies, biases, or errors. RoleRound makes no warranty that
              AI feedback is accurate, complete, or appropriate for any specific employer or role.
              You should use feedback as one input among many — not as a definitive assessment of
              your interview readiness. We are not liable for decisions made based on AI-generated
              feedback.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Intellectual Property</h2>
            <p className="text-muted-foreground">
              RoleRound owns all intellectual property in the Service, including the platform
              design, scoring system, system prompts, and AI feedback methodology. You retain
              ownership of your interview responses and any content you submit. By submitting
              content, you grant us a limited license to process it for the purpose of providing the
              Service. We do not use your interview responses to train AI models without your
              explicit consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Privacy</h2>
            <p className="text-muted-foreground">
              Your use of RoleRound is also governed by our{' '}
              <a href="/privacy" className="text-primary underline underline-offset-2">
                Privacy Policy
              </a>
              , which is incorporated into these Terms by reference. By using the Service, you
              consent to the data practices described in the Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              9. Disclaimers and Limitation of Liability
            </h2>
            <p className="text-muted-foreground mb-3">
              The Service is provided "as is" and "as available" without warranties of any kind,
              express or implied. We do not warrant that the Service will be uninterrupted,
              error-free, or that AI feedback will meet your expectations.
            </p>
            <p className="text-muted-foreground">
              To the maximum extent permitted by law, RoleRound shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages, including lost
              profits or opportunities, arising from your use of or inability to use the Service —
              even if we have been advised of the possibility of such damages. Our total liability
              to you for any claim arising from use of the Service shall not exceed the amount you
              paid us in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Termination</h2>
            <p className="text-muted-foreground">
              You may stop using the Service and delete your account at any time from Settings. We
              may suspend or terminate your access if you violate these Terms, with or without
              notice depending on the severity of the violation. Upon termination, your right to use
              the Service ceases immediately. Sections on intellectual property, disclaimers,
              limitation of liability, and governing law survive termination.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We may update these Terms as the Service evolves. If we make material changes, we will
              notify you by email or by a notice in the app at least 14 days before the change takes
              effect. Continued use of the Service after the effective date constitutes your
              acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms are governed by the laws of the State of California, without regard to its
              conflict of law principles. Any disputes arising from these Terms or your use of the
              Service shall be resolved in the state or federal courts located in California, and
              you consent to personal jurisdiction in those courts.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Contact</h2>
            <p className="text-muted-foreground">
              Questions about these Terms? Email us at{' '}
              <a
                href="mailto:support@roleround.com"
                className="text-primary underline underline-offset-2"
              >
                support@roleround.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
